<template>
  <div>
    <!-- Cabecera -->
    <div class="st-header mb-2">
      <div class="d-flex align-items-center">
        <div class="st-header__icon">
          <i class="fa-solid fa-file-lines"></i>
        </div>
        <div>
          <h2 class="mb-0 font-weight-bolder">Statements</h2>
          <small class="text-muted">Todos los statements generados, con su PDF archivado</small>
        </div>
      </div>
      <b-button variant="primary" @click="abrirNuevo">
        <i class="fa-solid fa-plus mr-50"></i> Nuevo statement
      </b-button>
    </div>

    <!-- Modal: generar statement (cliente + pedido o rango de fechas) -->
    <b-modal
      v-model="nuevo.visible"
      title="Generar statement"
      centered
      body-class="st-nuevo-body"
      :ok-disabled="!nuevo.customerId || nuevo.generando"
      :ok-title="nuevo.generando ? 'Generando…' : 'Generar PDF'"
      cancel-title="Cancelar"
      @ok.prevent="generar"
    >
      <label class="font-weight-bold mb-50">Cliente</label>
      <v-select
        v-model="nuevo.customerId"
        :options="clienteOptions"
        :reduce="o => o.value"
        label="text"
        placeholder="Busca un cliente…"
        class="select-styled mb-2"
        @input="cargarPedidosCliente"
      />

      <template v-if="nuevo.customerId">
        <label class="font-weight-bold mb-50">Pedido</label>
        <v-select
          v-model="nuevo.pedidoId"
          :options="pedidoOptions"
          :reduce="o => o.value"
          label="text"
          :clearable="false"
          class="select-styled mb-2"
        />

        <b-row v-if="!nuevo.pedidoId">
          <b-col cols="6">
            <label class="font-weight-bold mb-50">Desde</label>
            <b-form-input v-model="nuevo.desde" type="date" />
            <small class="text-muted">Vacío = todo el histórico</small>
          </b-col>
          <b-col cols="6">
            <label class="font-weight-bold mb-50">Hasta</label>
            <b-form-input v-model="nuevo.hasta" type="date" />
          </b-col>
        </b-row>
      </template>

      <div v-if="nuevo.generando" class="text-center mt-2">
        <b-spinner small class="align-middle mr-50" /> Generando PDF…
      </div>
    </b-modal>

    <b-card no-body>
      <b-card-body>
        <b-input-group class="mb-1">
          <b-input-group-prepend is-text>
            <i class="fa-solid fa-magnifying-glass text-muted"></i>
          </b-input-group-prepend>
          <b-form-input
            v-model="q"
            type="search"
            placeholder="Buscar por número o cliente…"
          />
        </b-input-group>

        <b-table
          :items="statementsFiltrados"
          :fields="fields"
          :busy="cargando"
          responsive
          show-empty
          empty-text="Todavía no se ha generado ningún statement"
          class="mb-0"
        >
          <template #table-busy>
            <div class="text-center my-2"><b-spinner class="align-middle mr-50" /> Cargando…</div>
          </template>

          <template #cell(numero)="row">
            <span class="font-weight-bold">{{ row.item.numero }}</span>
          </template>

          <template #cell(cliente)="row">
            <b-link class="font-weight-bold" :to="{ name: 'customer-profile', params: { id: row.item.customerId } }">
              {{ row.item.cliente }}
            </b-link>
          </template>

          <template #cell(createdAt)="row">
            {{ formatFecha(row.item.createdAt) }}
          </template>

          <template #cell(alcance)="row">
            <template v-if="row.item.pedidoRef">{{ row.item.pedidoRef }}</template>
            <template v-else>
              {{ row.item.desde ? formatFechaCorta(row.item.desde) : 'Inicio' }} –
              {{ row.item.hasta ? formatFechaCorta(row.item.hasta) : '—' }}
            </template>
          </template>

          <template #cell(outstanding)="row">
            <span class="font-weight-bold">{{ row.item.simbolo }}{{ row.item.outstanding }}</span>
            <small class="text-muted d-block">pendiente</small>
          </template>

          <template #cell(usuarioNombre)="row">
            <small class="text-muted">{{ row.item.usuarioNombre || '—' }}</small>
          </template>

          <template #cell(acciones)="row">
            <b-button
              size="sm"
              variant="flat-secondary"
              class="btn-icon"
              title="Ver / imprimir el PDF archivado"
              :disabled="pdfId === row.item.id"
              @click="verPdf(row.item)"
            >
              <b-spinner v-if="pdfId === row.item.id" small />
              <i v-else class="fa-solid fa-file-pdf"></i>
            </b-button>
          </template>
        </b-table>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import axios from '@axios'
import vSelect from 'vue-select'

export default {
  components: { vSelect },
  data() {
    return {
      statements: [],
      q: '',
      cargando: false,
      pdfId: null,
      clientes: [],
      pedidosCliente: [],
      nuevo: {
        visible: false,
        customerId: null,
        pedidoId: null,
        desde: '',
        hasta: new Date().toISOString().slice(0, 10),
        generando: false,
      },
      fields: [
        { key: 'numero', label: 'Número', class: 'text-nowrap', thStyle: { width: '150px' } },
        { key: 'cliente', label: 'Cliente' },
        { key: 'createdAt', label: 'Generado', class: 'text-nowrap' },
        { key: 'alcance', label: 'Pedido / Periodo' },
        { key: 'outstanding', label: 'Outstanding' },
        { key: 'usuarioNombre', label: 'Usuario' },
        { key: 'acciones', label: '', class: 'text-right' },
      ],
    }
  },
  computed: {
    clienteOptions() {
      return this.clientes.map(c => ({
        value: c.id,
        text: c.companyName || [c.nombre, c.apellidos].filter(Boolean).join(' ') || c.email || `Cliente ${c.id}`,
      })).sort((a, b) => a.text.localeCompare(b.text, 'es'))
    },
    pedidoOptions() {
      return [
        { value: null, text: 'Todos los pedidos — por rango de fechas' },
        ...this.pedidosCliente.map(p => ({
          value: p.id,
          text: `${p.ref} — ${this.formatFechaCorta(p.createdAt)} — ${p.simbolo}${p.total}`,
        })),
      ]
    },
    statementsFiltrados() {
      const term = this.q.trim().toLowerCase()
      if (!term) return this.statements
      return this.statements.filter(st =>
        st.numero.toLowerCase().includes(term)
        || (st.cliente || '').toLowerCase().includes(term))
    },
  },
  created() {
    axios.post('/uso', { seccion: 'statements' }).catch(() => {})
    this.cargar()
  },
  methods: {
    formatFecha(f) {
      if (!f) return '—'
      const d = new Date(f)
      return Number.isNaN(d.getTime()) ? f : d.toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })
    },
    formatFechaCorta(f) {
      if (!f) return '—'
      const d = new Date(f)
      return Number.isNaN(d.getTime()) ? f : d.toLocaleDateString('es-ES')
    },
    cargar() {
      this.cargando = true
      axios
        .get('/customers/statements/todos')
        .then(({ data }) => { this.statements = data })
        .catch(() => {
          this.$bvToast.toast('No se pudieron cargar los statements', { title: 'Error', variant: 'danger', solidColor: true })
        })
        .finally(() => { this.cargando = false })
    },
    abrirNuevo() {
      this.nuevo = {
        visible: true,
        customerId: null,
        pedidoId: null,
        desde: '',
        hasta: new Date().toISOString().slice(0, 10),
        generando: false,
      }
      this.pedidosCliente = []
      if (!this.clientes.length) {
        axios
          .get('/customers')
          .then(({ data }) => { this.clientes = data || [] })
          .catch(() => {})
      }
    },
    cargarPedidosCliente() {
      this.nuevo.pedidoId = null
      this.pedidosCliente = []
      if (!this.nuevo.customerId) return
      const cliente = this.clientes.find(c => c.id === this.nuevo.customerId)
      axios
        .get('/escaner/pedidos', {
          params: {
            customerId: this.nuevo.customerId,
            email: (cliente && cliente.email) || undefined,
          },
        })
        .then(({ data }) => { this.pedidosCliente = data || [] })
        .catch(() => {})
    },
    generar() {
      this.nuevo.generando = true
      const pedidoId = this.nuevo.pedidoId || undefined
      axios
        .get(`/customers/${this.nuevo.customerId}/statement/pdf`, {
          responseType: 'blob',
          params: {
            pedidoId,
            desde: pedidoId ? undefined : (this.nuevo.desde || undefined),
            hasta: pedidoId ? undefined : (this.nuevo.hasta || undefined),
          },
        })
        .then(({ data }) => {
          const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
          window.open(url, '_blank')
          this.nuevo.visible = false
          this.cargar()
        })
        .catch(() => {
          this.$bvToast.toast('No se pudo generar el statement', { title: 'Error', variant: 'danger', solidColor: true })
        })
        .finally(() => { this.nuevo.generando = false })
    },
    verPdf(st) {
      this.pdfId = st.id
      axios
        .get(`/customers/statements/${st.id}/pdf`, { responseType: 'blob' })
        .then(({ data }) => {
          const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
          window.open(url, '_blank')
        })
        .catch(() => {
          this.$bvToast.toast('No se pudo abrir el PDF', { title: 'Error', variant: 'danger', solidColor: true })
        })
        .finally(() => { this.pdfId = null })
    },
  },
}
</script>

<style lang="scss">
// Sin scoped: el modal se monta en el body y los estilos scoped no le llegan
@import '../@core/scss/vue/libs/vue-select.scss';

.st-nuevo-body {
  padding: 2rem 2.25rem 2.5rem;
}
</style>

<style lang="scss" scoped>
.st-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;

  &__icon {
    width: 46px;
    height: 46px;
    border-radius: 12px;
    background: linear-gradient(135deg, #7367f0, #9e95f5);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    margin-right: 1rem;
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.35);
  }
}
</style>
